import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { KPI } from "../components/UIBits";
import {
  summarize,
  getAllEvents,
  metricEvents,
  previousWindow,
  defaultRange,
  describeReliability,
  kpiConfigure,
} from "../lib/kpi";
import { loadData, saveData } from "../api/awsClient";
import FancyDate from "../components/FancyDate";
import PilotCard, { usePilotData } from "../components/PilotCard";
import { todayInZone, workspaceTimeZone } from "../lib/pilot";
import { toast } from "../lib/toast";
import { dialog } from "../lib/dialog";

// KPI events change at most once per worker tick, so a 30 s poll is plenty.
const POLL_MS = 30000;
const MAX_BACKOFF_MS = 120000;
const HIDDEN_POLL_MS = 60000;
const NO_EVENTS = [];

const pct = (v) => (v == null ? null : Number(v) * 100);
const fmtPct = (v) => `${v.toFixed(1)}%`;

export default function KPIs() {
  const {
    workspaceOwnerEmail,
    canManageWorkspace,
    workspaceDocStatus = "ready",
    apiBase,
    slackPrefs,
    // settings.pilot lives in App state; this page is its home and saves it
    // through App's saveSettingsPatch (only that key, merged by the server),
    // so the Control Room sees the change without a reload.
    pilot = null,
    setPilot,
    saveSettingsPatch,
  } = useOutletContext();
  // Never written while the workspace document failed to load.
  const canManagePilot =
    !!canManageWorkspace &&
    workspaceDocStatus === "ready" &&
    typeof saveSettingsPatch === "function";
  const [email, setEmail] = useState("");
  const [range, setRange] = useState(() => defaultRange());
  // While the user has not picked dates, the window follows today, so a page
  // left open past midnight moves to the new day on the next poll.
  const followDefaultRef = useRef(true);

  const [isResetting, setIsResetting] = useState(false);
  // Raw events are kept so a metric can be opened up and shown as the actual
  // things that happened, rather than only a number. null until the first load.
  const [allEvents, setAllEvents] = useState(null);
  const [detail, setDetail] = useState(null);

  const events = allEvents || NO_EVENTS;

  /* ----- Pilot card (components/PilotCard.js) ----- */
  const pilotToday = todayInZone(new Date(), workspaceTimeZone(slackPrefs));
  const [pilotSaveVersion, setPilotSaveVersion] = useState(0);
  const [pilotBusy, setPilotBusy] = useState(false);
  const [pilotSaveError, setPilotSaveError] = useState("");
  const pilotData = usePilotData({
    pilot,
    today: pilotToday,
    apiBase,
    ownerEmail: email,
    // A landed save re-reads the server's status for the new setting.
    saveVersion: pilotSaveVersion,
    // This page already polls the KPI events; the card's activity line
    // reuses them.
    events: allEvents,
  });
  const savePilot = useCallback(
    async (next) => {
      if (!canManagePilot) return;
      const previous = pilot;
      setPilotSaveError("");
      setPilotBusy(true);
      setPilot?.(next);
      try {
        await saveSettingsPatch({ pilot: next });
        setPilotSaveVersion((v) => v + 1);
      } catch {
        setPilot?.(previous);
        setPilotSaveError("Couldn't save the pilot. Try again.");
      } finally {
        setPilotBusy(false);
      }
    },
    [canManagePilot, pilot, setPilot, saveSettingsPatch],
  );
  // The Control Room's baseline banner links here (/kpis#pilot). The layout
  // scrolls to the top after a page mounts, so wait a tick.
  useEffect(() => {
    if (typeof window === "undefined" || window.location.hash !== "#pilot") {
      return undefined;
    }
    const t = setTimeout(() => {
      document
        .getElementById("pilot")
        ?.scrollIntoView?.({ behavior: "smooth", block: "start" });
    }, 50);
    return () => clearTimeout(t);
  }, []);
  const baseRange = useMemo(
    () => previousWindow(range.start, range.end),
    [range.start, range.end],
  );
  const current = useMemo(
    () => summarize(events, range.start, range.end),
    [events, range.start, range.end],
  );
  const baseline = useMemo(
    () => summarize(events, baseRange.start, baseRange.end),
    [events, baseRange.start, baseRange.end],
  );

  const refreshEvents = useCallback(async () => {
    if (!email) return;
    const list = await getAllEvents(email);
    setAllEvents(Array.isArray(list) ? list : []);
  }, [email]);

  useEffect(() => {
    const nextEmail = String(workspaceOwnerEmail || "").trim().toLowerCase();
    setEmail(nextEmail);
    if (nextEmail) kpiConfigure({ email: nextEmail });
  }, [workspaceOwnerEmail]);

  // live KPI polling
  useEffect(() => {
    if (!email) return;

    let alive = true;
    let timer = null;
    let backoffMs = POLL_MS;

    const tick = async () => {
      if (followDefaultRef.current) {
        const next = defaultRange();
        setRange((r) =>
          r.start === next.start && r.end === next.end ? r : next,
        );
      }
      try {
        await refreshEvents();
        backoffMs = POLL_MS;
      } catch {
        backoffMs = Math.min(Math.round(backoffMs * 1.75), MAX_BACKOFF_MS);
      }
      if (!alive) return;
      const visible = document.visibilityState === "visible";
      const delay = visible ? backoffMs : Math.max(backoffMs, HIDDEN_POLL_MS);
      timer = setTimeout(tick, delay);
    };

    tick();

    const onVis = () => {
      if (document.visibilityState === "visible") {
        clearTimeout(timer);
        tick();
      }
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      alive = false;
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [email, refreshEvents]);

  const handleResetKpis = async () => {
    if (!canManageWorkspace) return;
    const confirmed = await dialog.confirm({
      title: "Reset KPIs?",
      body: "This deletes all KPI events.",
      confirmLabel: "Reset KPIs",
      danger: true,
    });
    if (!confirmed) return;
    setIsResetting(true);
    try {
      // --- S3: clear settings.kpiEvents ---
      if (email) {
        const remote = await loadData(email).catch(() => null);
        const version = remote?.version ?? 1;
        const rSettings = remote?.settings || remote || {};
        const rTasks = remote?.tasks || {};
        const mergedSettings = { ...rSettings, kpiEvents: [] };
        const payload = {
          version,
          updatedAt: new Date().toISOString(),
          settings: mergedSettings,
          tasks: rTasks,
        };
        await saveData(email, payload);
      }

      // --- DB: clear KPI events (best effort) ---
      await fetch("https://api.bossdrop.ai/api/kpi/reset", {
        method: "DELETE",
        credentials: "include",
      }).catch(() => {});

      await refreshEvents();
      toast.success("KPIs reset.");
    } catch (e) {
      console.error(e);
      toast.error("Couldn't reset KPIs. Please try again.");
    } finally {
      setIsResetting(false);
    }
  };

  const setRangeField = (field, value) => {
    followDefaultRef.current = false;
    setRange((r) => ({ ...r, [field]: value }));
  };

  const noData =
    allEvents != null && current.attempts === 0 && !current.automationsSkipped;

  return (
    <section className="panel">
      <div className="kpiHeader">
        <div>
          <h2 style={{ marginBottom: 6 }}>Pilot KPI Tracker</h2>
          <div className="muted" style={{ marginTop: -2 }}>
            {range.start} → {range.end} (baseline: {baseRange.start} →{" "}
            {baseRange.end})
          </div>
          <div className="muted" style={{ marginTop: 6 }}>
            These KPIs track the coordinator's work BossDrop does: exec
            reports, status pings, escalations, tasks, meetings, email replies,
            and duplicate work prevented.
          </div>
          {!canManageWorkspace ? (
            <div className="muted" style={{ marginTop: 8 }}>
              Read-only view. Only admins can reset KPI data.
            </div>
          ) : null}
        </div>

        <div className="kpiHeaderActions">
          <button
            className="btn btnDanger"
            type="button"
            onClick={handleResetKpis}
            disabled={isResetting || !canManageWorkspace}
          >
            {isResetting ? "Resetting…" : "Reset KPIs"}
          </button>
        </div>
      </div>

      {/* The pilot's home: start it, follow its phase and team metrics,
          end it. Styled as the Control Room's cards are. */}
      <div className="controlRoom kpiPilot">
        <PilotCard
          pilot={pilot}
          onChange={savePilot}
          canManage={canManagePilot}
          today={pilotToday}
          state={pilotData.state}
          metrics={pilotData.metrics}
          metricsStatus={pilotData.metricsStatus}
          activity={pilotData.activity}
          learned={pilotData.learned}
          busy={pilotBusy}
          saveError={pilotSaveError}
        />
      </div>

      <div className="panelSub" style={{ marginTop: 12 }}>
        {/* Three across; stacked on a phone (index.css .kpiRange). */}
        <div className="formRow kpiRange">
          <label>
            Start
            <FancyDate
              value={range.start}
              onChange={(day) => setRangeField("start", day)}
              ariaLabel="Start"
            />
          </label>
          <label>
            End
            <FancyDate
              value={range.end}
              onChange={(day) => setRangeField("end", day)}
              ariaLabel="End"
            />
          </label>
          <button
            className="btn"
            type="button"
            onClick={() => {
              followDefaultRef.current = true;
              setRange(defaultRange());
            }}
          >
            Last 28 days
          </button>
        </div>
      </div>

      {/* ===== Outcome KPIs (pilot success) ===== */}
      <h3 style={{ marginTop: 14 }}>Outcome</h3>
      <div className="kpiGrid" style={{ marginTop: 8 }}>
        <KpiTile
          label="Exec reports delivered"
          group="Outcome"
          metric="reports"
          describe="Executive briefs BossDrop generated and sent. Each entry is one delivered report."
          onOpen={setDetail}
          cur={current.reports}
          base={baseline.reports}
          higherIsBetter
        />
        <KpiTile
          label="Status pings sent"
          group="Outcome"
          metric="pings"
          describe="Direct messages asking an owner for a status update, so nobody had to chase it manually. Counted once per person per day."
          onOpen={setDetail}
          cur={current.pingsSent}
          base={baseline.pingsSent}
          higherIsBetter
        />
        <KpiTile
          label="Clarifications sent"
          group="Outcome"
          metric="clarifications"
          describe="Questions BossDrop asked when a task was ambiguous, instead of guessing or acting wrongly."
          onOpen={setDetail}
          cur={current.clarifysSent}
          base={baseline.clarifysSent}
          higherIsBetter
        />
        <KpiTile
          label="Risks escalated"
          group="Outcome"
          metric="escalations"
          describe="Blockers and risks routed to someone with authority, with context and a specific ask."
          onOpen={setDetail}
          cur={current.escalationsSent}
          base={baseline.escalationsSent}
          higherIsBetter
        />
        <KpiTile
          label="Tasks created"
          group="Outcome"
          metric="tasksCreated"
          describe="Tasks opened in Asana, Jira or Planner with an owner and a due date."
          onOpen={setDetail}
          cur={current.tasksCreated}
          base={baseline.tasksCreated}
          higherIsBetter
        />
        <KpiTile
          label="Tasks updated"
          group="Outcome"
          metric="tasksUpdated"
          describe="Existing tasks whose status, owner or date BossDrop changed to match reality."
          onOpen={setDetail}
          cur={current.tasksUpdated}
          base={baseline.tasksUpdated}
          higherIsBetter
        />
        <KpiTile
          label="Meetings scheduled"
          group="Outcome"
          metric="meetings"
          describe="Calendar holds created, only when a decision genuinely needed people in a room."
          onOpen={setDetail}
          cur={current.meetings}
          base={baseline.meetings}
          higherIsBetter
        />
        <KpiTile
          label="Duplicate meetings prevented"
          group="Outcome"
          metric="duplicateMeetingsPrevented"
          describe="Meetings BossDrop did not book because the same meeting was already on the calendar or already being booked. Counted once per meeting title per day; a skip it could not verify is left out."
          onOpen={setDetail}
          cur={current.duplicateMeetingsPrevented}
          base={baseline.duplicateMeetingsPrevented}
          higherIsBetter
        />
        <KpiTile
          label="Reschedules"
          group="Outcome"
          metric="reschedules"
          describe="Meetings moved after being booked. Lower is better: frequent moves usually mean the original time was chosen with bad availability data. Shows a dash when the window has no meetings to measure."
          onOpen={setDetail}
          cur={current.reschedules}
          base={baseline.reschedules}
          lowerIsBetter
        />
      </div>

      {/* ===== Quality + Adoption ===== */}
      <h3 style={{ marginTop: 14 }}>Adoption & Quality</h3>
      <div className="kpiGrid" style={{ marginTop: 8 }}>
        <KpiTile
          label="Reliability"
          group="Adoption & Quality"
          metric="reliability"
          describe="Share of attempted automations that completed. Only failed actions lower it; setup warnings (a teammate BossDrop could not resolve, a missing bot permission) are in the audit log. The list shows the failures."
          onOpen={setDetail}
          cur={pct(current.successRate)}
          base={pct(baseline.successRate)}
          note={describeReliability(current)}
          higherIsBetter
          format={fmtPct}
        />
        <KpiTile
          label="Decision Rate"
          group="Adoption & Quality"
          metric="decisionRate"
          describe="Share of planned actions that passed the confidence and approval gates and were actually executed, rather than being held back. Actions that were attempted and failed count toward Reliability, not here."
          onOpen={setDetail}
          cur={pct(current.executionRate)}
          base={pct(baseline.executionRate)}
          higherIsBetter
          format={fmtPct}
        />
        <KpiTile
          label="Automations executed"
          group="Adoption & Quality"
          metric="executed"
          describe="Total actions carried out across all types in this window, email replies included."
          onOpen={setDetail}
          cur={current.automationsExecuted}
          base={baseline.automationsExecuted}
          higherIsBetter
        />
        <KpiTile
          label="Automations skipped"
          group="Adoption & Quality"
          metric="skipped"
          describe="Actions deliberately withheld: duplicates, below-confidence, or blocked by an approval gate. Skips are the safety system working, not failures."
          onOpen={setDetail}
          cur={current.automationsSkipped}
          base={baseline.automationsSkipped}
          lowerIsBetter
        />
        <KpiTile
          label="Active automation days"
          group="Adoption & Quality"
          metric="activeDays"
          describe="Days on which BossDrop did at least one thing. A good proxy for whether it is genuinely embedded in the operating rhythm."
          onOpen={setDetail}
          cur={current.activeDays}
          base={baseline.activeDays}
          higherIsBetter
        />
        <KpiTile
          label="People impacted"
          group="Adoption & Quality"
          metric="peopleImpacted"
          describe="Distinct people who received a ping, clarification, escalation, task, meeting invite, report or email reply from BossDrop. Failed, blocked and skipped sends are not counted."
          onOpen={setDetail}
          cur={current.peopleImpacted}
          base={baseline.peopleImpacted}
          higherIsBetter
        />
        <KpiTile
          label="KPI events stored"
          group="Adoption & Quality"
          describe="Derived: total instrumented events retained for this window. Mostly a data-health check."
          onOpen={setDetail}
          cur={current.eventsStored}
          base={baseline.eventsStored}
          higherIsBetter
        />
      </div>

      {/* ===== Efficiency ===== */}
      <h3 style={{ marginTop: 14 }}>Efficiency</h3>
      <div className="kpiGrid" style={{ marginTop: 8 }}>
        <KpiTile
          label="Estimated hours saved"
          group="Efficiency"
          describe="Derived: minutes saved converted to hours, using the per-action time assumptions listed under Minutes saved."
          onOpen={setDetail}
          cur={current.minutesSaved / 60}
          base={baseline.minutesSaved / 60}
          higherIsBetter
          format={(v) => v.toFixed(1)}
        />
        <KpiTile
          label="Avg exec report time"
          group="Efficiency"
          metric="reportTime"
          describe="How long each executive brief took to produce end to end. Lower is better."
          onOpen={setDetail}
          cur={current.avgReportMinutes}
          base={baseline.avgReportMinutes}
          lowerIsBetter
          format={(v) => `${v.toFixed(1)} min`}
        />
        <KpiTile
          label="Minutes saved"
          group="Efficiency"
          describe="Derived from action counts and per-action time assumptions (30 min per exec report, 10 per duplicate meeting prevented, 6 per escalation, 3 per task created, meeting scheduled or email reply, 2 per status ping, clarification or task update). Treat it as an indicator, not a measurement."
          onOpen={setDetail}
          cur={current.minutesSaved}
          base={baseline.minutesSaved}
          higherIsBetter
          format={(v) => `${v.toFixed(1)} min`}
          hideDelta // a derived value; comparing vs baseline % doesn't make sense here
        />
      </div>

      {noData ? (
        <p className="muted" style={{ marginTop: 12 }}>
          No KPI activity yet. Run any automation (weekly exec report, daily
          status ping, escalation, meeting scheduling, or task creation) to
          start populating pilot KPIs.
        </p>
      ) : null}

      <KpiDetail
        detail={detail}
        events={events}
        range={range}
        onClose={() => setDetail(null)}
      />
    </section>
  );
}

const countText = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/** Human summary of one raw KPI event. */
function describeEvent(e) {
  const m = e?.meta || {};
  if (e?.type === "brain_execute_summary") {
    const bits = [
      `${countText(m.executedCount)} executed`,
      `${countText(m.skippedCount)} skipped`,
    ];
    if (m.failedCount != null) bits.push(`${countText(m.failedCount)} failed`);
    return bits.join(" · ");
  }
  if (e?.type === "brain_dedup") {
    return `${countText(m.proposed)} proposed · ${countText(m.kept)} kept`;
  }
  const list = (v) => (Array.isArray(v) ? v.filter(Boolean).join(", ") : v);
  const who =
    list(m.to) ||
    list(m.recipients) ||
    m.owner ||
    m.ownerEmail ||
    m.assignee ||
    list(m.attendees) ||
    "";
  const what = e?.title || m.title || m.topic || m.about || m.subject || "";
  const bits = [];
  if (what) bits.push(what);
  if (who) bits.push(String(who));
  if (Number.isFinite(e?.ms)) bits.push(`${(e.ms / 60000).toFixed(1)} min`);
  if (m.type && /^action_/.test(String(e?.type))) bits.push(`action: ${m.type}`);
  if (m.status) bits.push(String(m.status));
  if (m.warningCode) bits.push(String(m.warningCode));
  else if (m.reason) bits.push(String(m.reason));
  if (m.err) bits.push(`error: ${m.err}`);
  return bits.join(" · ") || "No details recorded";
}

/** Slide-over panel showing what actually produced a metric. */
function KpiDetail({ detail, events, range, onClose }) {
  if (!detail) return null;
  const rows = detail.metric
    ? metricEvents(events, detail.metric, range.start, range.end)
    : [];
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${detail.label} detail`}
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(18,16,32,.62)",
        zIndex: 60,
        display: "flex",
        justifyContent: "flex-end",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(560px, 100%)",
          height: "100%",
          overflowY: "auto",
          background: "var(--panel, #1a1729)",
          borderLeft: "1px solid var(--border, rgba(255,255,255,.12))",
          padding: "20px 22px 32px",
          boxShadow: "-24px 0 60px rgba(0,0,0,.45)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontSize: 10,
                letterSpacing: ".12em",
                textTransform: "uppercase",
                color: "var(--muted)",
                fontWeight: 800,
              }}
            >
              {detail.group}
            </div>
            <h3 style={{ margin: "6px 0 0" }}>{detail.label}</h3>
          </div>
          <button type="button" className="btn" onClick={onClose}>
            Close
          </button>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: 14,
            margin: "16px 0 4px",
          }}
        >
          <div style={{ fontSize: 30, fontWeight: 800, lineHeight: 1 }}>
            {detail.valueText}
          </div>
          <div className="muted" style={{ fontSize: 13 }}>
            baseline {detail.baseText}
          </div>
        </div>
        {detail.note ? (
          <div className="muted" style={{ fontSize: 13 }}>
            {detail.note}
          </div>
        ) : null}

        {detail.describe ? (
          <p className="muted" style={{ marginTop: 12, fontSize: 13.5 }}>
            {detail.describe}
          </p>
        ) : null}

        <div style={{ marginTop: 20 }}>
          <div
            style={{
              fontSize: 10,
              letterSpacing: ".12em",
              textTransform: "uppercase",
              color: "var(--muted)",
              fontWeight: 800,
              marginBottom: 8,
            }}
          >
            {detail.metric
              ? `Underlying activity (${rows.length})`
              : "How this is calculated"}
          </div>

          {!detail.metric ? (
            <p className="muted" style={{ fontSize: 13.5 }}>
              This is a derived measure. It is computed from other events
              rather than recorded directly, so there is no raw list to show.
            </p>
          ) : rows.length ? (
            <div style={{ display: "grid", gap: 8 }}>
              {rows.slice(0, 200).map((e, i) => (
                <div
                  key={e.fp || `${e.ts}-${i}`}
                  style={{
                    border: "1px solid var(--card-bd-2, rgba(255,255,255,.10))",
                    borderRadius: 10,
                    padding: "9px 11px",
                    background: "var(--chip-bg, rgba(255,255,255,.03))",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 10,
                      fontSize: 11,
                      color: "var(--muted)",
                    }}
                  >
                    <span style={{ fontWeight: 700 }}>{e.type}</span>
                    <span>{new Date(e.ts).toLocaleString()}</span>
                  </div>
                  <div style={{ marginTop: 4, fontSize: 13.5 }}>
                    {describeEvent(e)}
                  </div>
                </div>
              ))}
              {rows.length > 200 ? (
                <div className="muted" style={{ fontSize: 12 }}>
                  Showing the 200 most recent of {rows.length}.
                </div>
              ) : null}
            </div>
          ) : (
            <p className="muted" style={{ fontSize: 13.5 }}>
              Nothing recorded in this window.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

const isNum = (v) => v != null && v !== "" && Number.isFinite(Number(v));

/**
 * KPI tile with correct delta logic:
 * - current or baseline null (nothing to measure) => "—", no badge
 * - baseline 0 & current 0 => 0.0% (neutral)
 * - baseline 0 & current != 0 => "NEW" (green if higherIsBetter, red if lowerIsBetter)
 * - baseline > 0 => % change shown; color determined by "better vs worse"
 */
export function KpiTile({
  label,
  cur,
  base,
  lowerIsBetter,
  higherIsBetter,
  format,
  hideDelta,
  group,
  metric,
  describe,
  note,
  onOpen,
}) {
  const c = isNum(cur) ? Number(cur) : null;
  const b = isNum(base) ? Number(base) : null;

  const isLowerBetter = !!lowerIsBetter && !higherIsBetter;
  const isHigherBetter =
    !!higherIsBetter || (!lowerIsBetter && !higherIsBetter);

  const fmt = format || ((v) => String(v));
  const show = (v) => (v == null ? "—" : fmt(v));

  const neutral = "var(--muted, #ccc)";
  const goodColor = "var(--state-ok, #8ee59b)";
  const badColor = "var(--state-bad, #ff9a9a)";

  let badgeText = null;
  let badgeColor = neutral;
  let arrow = "→";

  if (!hideDelta && c != null && b != null) {
    if (b === 0 && c === 0) {
      badgeText = "→ 0.0%";
    } else if (b === 0) {
      badgeText = "NEW";
      const good = isHigherBetter ? true : isLowerBetter ? false : true;
      badgeColor = good ? goodColor : badColor;
    } else {
      const rawChange = c - b;
      const change = (rawChange / b) * 100;
      arrow = rawChange === 0 ? "→" : rawChange > 0 ? "↑" : "↓";

      const good = isLowerBetter ? rawChange < 0 : rawChange > 0;
      badgeColor = rawChange === 0 ? neutral : good ? goodColor : badColor;

      const sign = rawChange === 0 ? "" : rawChange > 0 ? "+" : "−";
      badgeText = `${arrow} ${sign}${Math.abs(change).toFixed(1)}%`;
    }
  }

  const open = () =>
    onOpen?.({
      label,
      group,
      metric: metric || null,
      describe,
      note,
      valueText: show(c),
      baseText: show(b),
    });

  return (
    <div
      className="panelSub kpiTile"
      role="button"
      tabIndex={0}
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          open();
        }
      }}
      title="Open details"
      style={{ cursor: "pointer", position: "relative" }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 10,
          justifyContent: "space-between",
        }}
      >
        <KPI label={label} value={show(c)} />
        {badgeText ? (
          <span
            data-testid="kpi-delta"
            style={{
              fontSize: 12,
              padding: "2px 8px",
              borderRadius: 999,
              border: "1px solid var(--border, rgba(255,255,255,.12))",
              color: badgeColor,
              opacity: 0.95,
              whiteSpace: "nowrap",
            }}
            title={`Baseline ${show(b)} → Current ${show(c)}`}
          >
            {badgeText}
          </span>
        ) : null}
      </div>
      {note ? (
        <div className="muted" style={{ marginTop: 6, fontSize: 12 }}>
          {note}
        </div>
      ) : null}
      <div
        className="muted"
        style={{
          marginTop: 4,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 8,
        }}
      >
        <span>Baseline: {show(b)}</span>
        <span style={{ fontSize: 11, opacity: 0.75, whiteSpace: "nowrap" }}>
          Details →
        </span>
      </div>
    </div>
  );
}
